// The dialogue runner: what a conversation asks of a script engine. A runner starts a script (at a knot), hands out the
// next beat (a line, a set of choices, or the end) and takes the player's choice. Every line and choice comes as a
// string id for the game's strings table (decision 0061), never as text to show, so the words are localisable and an
// AI-voiced runner can hand back ids of lines it wrote into the table. The ink runner is the standard one (decision
// 0054); the JSON node runner (script.ts) and any test runner fit the same interface (decision 0055).

/** A line: its string id and who says it (null: the conversation's NPC). */
export interface LineBeat {
  kind: "line";
  id: string;
  speaker: string | null;
  /** Every tag on the line (`id:` and `speaker:` among them). */
  tags: readonly string[];
}

/** The player's options: each a string id. */
export interface ChoicesBeat {
  kind: "choices";
  choices: readonly { index: number; id: string; tags: readonly string[] }[];
}

/** One step of a conversation. */
export type DialogueBeat = LineBeat | ChoicesBeat | { kind: "end" };

/** A script engine behind a conversation. */
export interface DialogueRunner {
  /** The scripts it has. */
  scripts(): string[];
  /** Start `script` at `knot` (or its top). */
  start(script: string, knot?: string): void;
  /** The next beat; a line is consumed, choices stay until one is chosen. */
  next(): DialogueBeat;
  /** Take the choice with this index (from the last choices beat). */
  choose(index: number): void;
  /** Saved state: what each script remembers (visit counts, its own variables) and a conversation in progress. */
  snapshot(): unknown;
  restore(data: unknown): void;
}

/** A line or choice reached the player without a string id. */
export class MissingStringIdError extends Error {
  constructor(
    readonly script: string,
    readonly text: string,
  ) {
    super(`dialogue: a line in "${script}" has no string id ("#id:…"): ${JSON.stringify(text.trim())}`);
    this.name = "MissingStringIdError";
  }
}

/** The value of the first `name:` tag, or null. */
export function tagValue(tags: readonly string[], name: string): string | null {
  for (const t of tags) {
    const s = t.trim();
    if (s.startsWith(`${name}:`)) return s.slice(name.length + 1).trim();
  }
  return null;
}

/** A line's estimated time on screen (s): `1.2 + 0.045 per character` (suggestion: world-anchored bubbles). */
export const lineSeconds = (text: string, o: { base?: number; perChar?: number } = {}): number => (o.base ?? 1.2) + (o.perChar ?? 0.045) * text.length;
